/**
 * 权益中心 API
 * 
 * 提供使用记录查询、月度汇总、导出等功能
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-10-26
 */

import { http } from '@/utils/http'
import { unwrap } from '@/utils/apiClient'

/**
 * 权益台账查询参数
 */
export interface EntitlementRecordQuery {
  page?: number
  pageSize?: number
  sourceType?: string // order/admin_grant/admin_compensate/refund_deduct/promotion/reward
  startTime?: string  // YYYY-MM-DD HH:mm:ss
  endTime?: string    // YYYY-MM-DD HH:mm:ss
}

/**
 * 权益台账记录项（匹配后端 EntitlementRecordInfo）
 */
export interface EntitlementRecordItem {
  id: number
  createdAt: string
  userId: number
  username?: string
  sourceType: string      // 来源类型
  referenceId: string     // 关联ID
  referenceNo: string     // 来源单号
  packageName: string     // 套餐名称
  delta: number           // 额度变化（正数=增加，负数=减少）
  balanceAfter: number    // 变化后余额
  effectiveAt: string     // 生效时间
  expireAt?: string       // 过期时间
  remark: string          // 备注
  createdBy?: number      // 操作员ID
  operatorName?: string   // 操作员名称
  ipAddress?: string      // 操作IP
}

/**
 * 权益台账响应
 */
export interface EntitlementRecordResponse {
  list: EntitlementRecordItem[]
  total: number
  page: number
  pageSize: number
}

/**
 * 月度汇总
 */
export interface MonthlySummary {
  month: string           // YYYY-MM
  totalTasks: number
  totalCharacters: number
  totalDuration: number
  totalCredits: number
  avgDailyUsage: number
  completedTasks: number
  failedTasks: number
}

/**
 * 权益汇总响应
 */
export interface EntitlementSummaryResponse {
  currentMonth: MonthlySummary
  history: MonthlySummary[]
}

/**
 * 权益中心 API
 */
export const entitlementApi = {
  /**
   * 获取权益台账（用户端）
   */
  async getEntitlementRecords(query: EntitlementRecordQuery): Promise<EntitlementRecordResponse> {
    return unwrap(() =>
      http.get<EntitlementRecordResponse>('/api/v1/user/entitlement-records', {
        params: query
      })
    )
  },

  /**
   * 获取月度汇总
   */
  async getMonthlySummary(months: number = 6): Promise<EntitlementSummaryResponse> {
    return unwrap(() =>
      http.get<EntitlementSummaryResponse>('/api/v1/entitlement/summary', {
        params: { months }
      })
    )
  },

  /**
   * 获取当前用户权益
   */
  async getEntitlements(): Promise<EntitlementResponse> {
    return unwrap(() =>
      http.get<EntitlementResponse>('/api/v1/me/entitlements')
    )
  }
}

/**
 * 权益响应结构
 */
export interface EntitlementResponse {
  userTier: string
  updatedAt: string
  task: {
    maxBatchSize: number
    maxConcurrent: number
    maxDailyTasks: number
    priorityLevel: number
    hasAdvancedFeatures: boolean
  }
  conversion: {
    maxTextLength: number
    maxDailyChars: number
    supportedVoices: string[]
    hasPremiumVoices: boolean
    hasSSMLSupport: boolean
    hasEmotionControl: boolean
  }
  storage: {
    maxHistoryDays: number
    maxFileSize: number
    cloudStorageEnabled: boolean
    maxCloudStorage: number
  }
  usage?: {
    dailyCharsUsed: number
    dailyTasksUsed: number
    storageUsed: number
  }
  sources?: any
}

